import React from 'react';
import { Condition } from '../types';
import { X, ArrowRight } from 'lucide-react';
import { StatusBadge } from './StatusBadge';
interface PreviewCardChangesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  conditions: Condition[];
  pendingEdits: Record<string, Partial<Condition>>;
}
export function PreviewCardChangesModal({
  isOpen,
  onClose,
  onConfirm,
  conditions,
  pendingEdits
}: PreviewCardChangesModalProps) {
  if (!isOpen) return null;
  const editedConditions = conditions.filter(c => pendingEdits[c.id] && Object.keys(pendingEdits[c.id]).length > 0);
  if (editedConditions.length === 0) {
    return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-id="element-163">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-164">
          <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-165">
            No Changes Made
          </h3>
          <p className="text-sm text-gray-500 mb-6" data-id="element-166">
            You haven't made any changes to the selected conditions.
          </p>
          <div className="flex justify-end" data-id="element-167">
            <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200" data-id="element-168">
              Close
            </button>
          </div>
        </div>
      </div>;
  }
  const renderValue = (key: keyof Condition, value: any) => {
    if (value === null || value === undefined || value === '') return <span className="text-gray-400 italic" data-id="element-169">Empty</span>;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (key === 'status') return <StatusBadge status={value} data-id="element-170" />;
    if (key.toLowerCase().includes('date')) {
      return new Date(value).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
    return String(value);
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-171">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col" data-id="element-172">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-173">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-174">
            Preview Changes ({editedConditions.length} conditions)
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500" data-id="element-175">
            <X className="w-5 h-5" data-id="element-176" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1" data-id="element-177">
          <div className="space-y-6" data-id="element-178">
            {editedConditions.map(condition => {
            const edits = pendingEdits[condition.id];
            const updateKeys = Object.keys(edits) as Array<keyof Condition>;
            return <div key={condition.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50" data-id="element-179">
                  <div className="font-medium text-[#1C1C3A] mb-3" data-id="element-180">
                    {edits.name || condition.name}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-181">
                    {updateKeys.map(key => {
                  const oldValue = condition[key];
                  const newValue = edits[key];
                  if (oldValue === newValue) return null;
                  return <div key={key} className="flex items-center text-sm bg-white p-2 rounded border border-gray-100 shadow-sm" data-id="element-182">
                          <span className="w-1/3 text-gray-500 font-medium capitalize truncate pr-2" data-id="element-183">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </span>
                          <div className="w-2/3 flex items-center justify-between" data-id="element-184">
                            <div className="flex-1 truncate pr-2" data-id="element-185">
                              {renderValue(key, oldValue)}
                            </div>
                            <ArrowRight className="w-4 h-4 text-[#D44444] flex-shrink-0 mx-2" data-id="element-186" />
                            <div className="flex-1 truncate font-medium text-[#D44444]" data-id="element-187">
                              {renderValue(key, newValue)}
                            </div>
                          </div>
                        </div>;
                })}
                  </div>
                </div>;
          })}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3 rounded-b-lg" data-id="element-188">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1C1C3A]" data-id="element-189">
            Cancel
          </button>
          <button onClick={() => {
          onConfirm();
          onClose();
        }} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#D44444] hover:bg-[#B83333] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D44444]" data-id="element-190">
            Save All Changes
          </button>
        </div>
      </div>
    </div>;
}